/* eslint-disable react/no-unescaped-entities */
import React from "react";
import { useTranslation } from "react-i18next";
import { AuroraBackground } from "@/components/AuroraBackground";
import { Reveal } from "@/components/Reveal";
import { StoreBadgeGroup } from "@/components/StoreBadges";
import { Calendar, Brain, Sparkles, Users, Compass } from "lucide-react";

const FeaturesPage = () => {
  const { t } = useTranslation(["site", "landing"]);
  const tk = (k, fb) => t(`site:features.${k}`, fb);
  const SECTIONS = [
    {
      eyebrow: tk("calendarEyebrow"), title: tk("calendarTitle"), body: tk("calendarBody"),
      bullets: [tk("calendarB1"), tk("calendarB2"), tk("calendarB3")],
      img: "/assets/gallery/home-calendar-ios-2026.png", bg: "var(--aurora-analytics)", icon: <Calendar size={22} />,
    },
    {
      eyebrow: tk("coachEyebrow"), title: tk("coachTitle"), body: tk("coachBody"),
      bullets: [tk("coachB1"), tk("coachB2"), tk("coachB3")],
      img: "/assets/gallery/ai-coach.png", bg: "var(--aurora-coach)", icon: <Brain size={22} />, reverse: true,
    },
    {
      eyebrow: tk("partnerEyebrow"), title: tk("partnerTitle"), body: tk("partnerBody"),
      bullets: [tk("partnerB1"), tk("partnerB2"), tk("partnerB3")],
      img: "/assets/store-showcase/apple-workspace-inbox.webp", bg: "var(--aurora-emotional)", icon: <Users size={22} />,
    },
    {
      eyebrow: tk("ritualsEyebrow"), title: tk("ritualsTitle"), body: tk("ritualsBody"),
      bullets: [tk("ritualsB1"), tk("ritualsB2"), tk("ritualsB3")],
      img: "/assets/store-showcase/apple-journeys.webp", bg: "var(--aurora-quests)", icon: <Compass size={22} />, reverse: true,
    },
  ];

  return (
    <>
      <section className="relative pb-12">
        <AuroraBackground variant="hero" />
        <div className="relative mx-auto max-w-7xl px-5 lg:px-8 pt-8 lg:pt-12">
          <Reveal>
            <p className="eyebrow">{t("landing:features.kicker", tk("kickerOverride"))}</p>
            <h1 className="mt-3 font-serif text-[2.6rem] sm:text-5xl lg:text-[4.2rem] tracking-tight font-light leading-[1.05] max-w-4xl">
              {tk("title1")} <em className="italic aurora-shimmer-text">{tk("title2")}</em> {tk("title3")}
            </h1>
            <p className="mt-6 max-w-2xl text-white/72 text-lg font-light leading-relaxed">
              {tk("subtitle")}
            </p>
            <div className="mt-8">
              <StoreBadgeGroup startLabel={t("landing:hero.ctaPrimary", t("site:common.startFree"))} />
              <p className="mt-3 text-xs text-white/45 tracking-wide">{t("site:home.freeBadge")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="relative py-12 lg:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 space-y-20">
          {SECTIONS.map((s, i) => (
            <React.Fragment key={s.eyebrow}>
              <Reveal>
                <div className={`grid lg:grid-cols-12 gap-10 items-center aurora-card p-6 lg:p-12 ${s.reverse ? "lg:[direction:rtl]" : ""}`} style={{ background: s.bg }}>
                  <div className="lg:col-span-6 [direction:ltr]">
                    <span className="size-12 rounded-2xl bg-white/8 flex items-center justify-center text-[var(--aurora-gold)] mb-5">{s.icon}</span>
                    <p className="eyebrow">{s.eyebrow}</p>
                    <h2 className="mt-3 font-serif text-3xl sm:text-4xl tracking-tight font-light leading-tight">{s.title}</h2>
                    <p className="mt-4 text-white/75 font-light leading-relaxed">{s.body}</p>
                    <ul className="mt-6 space-y-2.5">
                      {s.bullets.map((b) => (
                        <li key={b} className="flex gap-2.5 text-white/80 font-light">
                          <Sparkles size={14} className="mt-1.5 text-[var(--aurora-gold)] shrink-0" />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="lg:col-span-6 [direction:ltr] flex justify-center">
                    <img src={s.img} alt={s.title} className="max-h-[520px] w-auto object-contain drop-shadow-2xl" loading="lazy" />
                  </div>
                </div>
              </Reveal>
              {i === 1 && (
                <Reveal>
                  <div className="aurora-glass p-8 sm:p-10 text-center">
                    <p className="font-serif text-2xl sm:text-3xl tracking-tight font-light">
                      {tk("midCtaTitle", "Stop rehearsing the conversation in the shower.")}
                    </p>
                    <p className="mt-3 text-white/70 font-light max-w-lg mx-auto">
                      {tk("midCtaBody", "Aurora helps you find the words while they still matter. Free, in two minutes.")}
                    </p>
                    <div className="mt-6 flex flex-wrap justify-center gap-3">
                      <StoreBadgeGroup startLabel={t("site:common.startFree")} />
                    </div>
                  </div>
                </Reveal>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      <section className="relative py-24">
        <div className="mx-auto max-w-5xl px-5 lg:px-8 text-center">
          <Reveal>
            <p className="eyebrow">{tk("closingEyebrow")}</p>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight font-light">
              {tk("closingTitle1")}{" "}
              <em className="italic aurora-shimmer-text">{tk("closingTitle2")}</em>
            </h2>
            <p className="mt-5 text-white/72 text-lg font-light max-w-2xl mx-auto">{tk("closingBody")}</p>
            <div className="mt-10 flex flex-wrap justify-center gap-3">
              <StoreBadgeGroup startLabel={t("site:common.startFree")} />
            </div>
            <p className="mt-4 text-xs text-white/45 tracking-wide">{t("site:common.freeFooter")}</p>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default FeaturesPage;
